import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Button, Form, FormFeedback, FormGroup, Input, Label } from 'reactstrap';
import { TypedMemo } from '../../../common/types';
import type { useEmailChange } from './useEmailChange';
import { emailChangeErrorMessage } from './emailChangeErrorMessage';
import { PanelBody } from '../../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Settings tabs; each part uses its own slice of it */
import cards from '../settingsCards.module.scss';
import styles from './account.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface Props {
  change: ReturnType<typeof useEmailChange>;
}

/** Step 1 (the new address) and step 2 (the code) of the email change (design settings-redesign b2). */
const EmailChangeFormComponent: React.FC<Props> = ({ change }) => {
  const intl = useIntl();
  const error = emailChangeErrorMessage(intl, change.errorCode);
  const cancelLabel = intl.formatMessage({ id: 'app.sml.settings.account.cancel', defaultMessage: 'Cancel' });

  const submitEmail = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      change.sendCode();
    },
    [change]
  );
  const submitCode = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      change.confirm();
    },
    [change]
  );

  if (change.step === 'enterNewEmail') {
    return (
      <PanelBody>
        <Form onSubmit={submitEmail} className={cards.stack}>
          <FormGroup className="mb-0">
            <Label for="newEmailInput">
              {intl.formatMessage({ id: 'app.sml.settings.account.newEmailLabel', defaultMessage: 'New email' })}
            </Label>
            <Input
              id="newEmailInput"
              type="email"
              required
              value={change.newEmail}
              onChange={(e) => change.setNewEmail(e.target.value)}
              autoComplete="email"
              autoFocus
              invalid={error !== null}
              data-clarity-mask="true"
            />
            {error !== null && <FormFeedback>{error}</FormFeedback>}
          </FormGroup>
          <div className="d-flex flex-wrap gap-2">
            <Button type="submit" color="primary" disabled={change.isSending || change.newEmail.length === 0}>
              {intl.formatMessage({ id: 'app.sml.settings.account.sendCode', defaultMessage: 'Send code' })}
            </Button>
            <Button type="button" color="secondary" outline onClick={change.cancel}>
              {cancelLabel}
            </Button>
          </div>
        </Form>
      </PanelBody>
    );
  }

  return (
    <PanelBody>
      <Form onSubmit={submitCode} className={cards.stack}>
        <FormGroup className="mb-0">
          <Label for="codeInput">
            {intl.formatMessage({ id: 'app.sml.settings.account.codeLabel', defaultMessage: 'Verification code' })}
          </Label>
          <Input
            id="codeInput"
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            required
            value={change.code}
            onChange={(e) => change.setCode(e.target.value.replace(/\D/g, ''))}
            autoFocus
            invalid={error !== null}
            className={styles.codeInput}
          />
          {error !== null && <FormFeedback>{error}</FormFeedback>}
        </FormGroup>
        <div className="d-flex flex-wrap gap-2">
          <Button type="submit" color="primary" disabled={change.isConfirming || change.code.length !== 6}>
            {intl.formatMessage({ id: 'app.sml.settings.account.confirmCode', defaultMessage: 'Confirm' })}
          </Button>
          <Button
            type="button"
            color="secondary"
            outline
            onClick={change.resend}
            disabled={change.resendInSeconds > 0 || change.isSending}
          >
            {change.resendInSeconds > 0
              ? intl.formatMessage(
                  { id: 'app.sml.settings.account.resendCooldown', defaultMessage: 'Resend in {seconds}s' },
                  { seconds: change.resendInSeconds }
                )
              : intl.formatMessage({ id: 'app.sml.settings.account.resend', defaultMessage: 'Resend code' })}
          </Button>
          <Button type="button" color="secondary" outline onClick={change.cancel}>
            {cancelLabel}
          </Button>
        </div>
      </Form>
    </PanelBody>
  );
};

export const EmailChangeForm = TypedMemo(EmailChangeFormComponent);
